import { useEffect, useState } from "react";
import "./CodePage.css";
import { AppShell } from "../app/AppShell";
import { Button } from "../components/Button";
import { Alert } from "../components/Card";
import { PageHead } from "../components/Page";
import { Field, Input } from "../components/Field";
import { hole, rich, tx } from "../i18n";

/* ══ 코드 분석 ══
   채택 시안 **안 B · 독립 화면(내비 새 메뉴)**, Figma node 88:42.

   **다른 점 — 내비에 「코드 분석」을 하나 더 단다.** GitPage 의 '접힌 띠' 틀을 그대로
   쓰되(ola-mops·gitea 가 이미 각자 화면을 가진 선례를 잇는다), 저장소 목록 대신
   이 설치가 아는 것(code MCP 가 세션에 붙는지)을 보여준다. `git__*` 클래스는
   GitPage.css 가 이미 전역으로 실어 둔 것을 그대로 쓴다(LogsPage 의 `git__spacer` 와
   같은 재사용).

   **API 는 `/code-mops/*` 를 그대로 쓴다** — 판정은 서버가 한다(`verdict`, `codemops.rs`).
   화면은 서버가 돌려준 갈래(`kind`)를 문구로 옮길 뿐 다시 판정하지 않는다.

   **주소는 이미 완성된 MCP 문이다** — 사람이 `…/mcp` 까지 적는다. 화면도 api 도 그
   뒤에 경로를 더 안 붙인다(`docs/decisions/code-mops.md` 「2026-10-07 뒤집힘」). */

const API = import.meta.env.VITE_B3XIN_API_URL ?? "/api";

type Conn = { baseUrl: string; tokenSet: boolean };
export type TestKind = "ok" | "no-tools" | "unauthorized" | "not-found" | "http-error" | "unreachable" | "no-address";
type TestResult = { kind: TestKind; status?: number; toolCount: number; ms?: number; detail?: string };

/** 시험 결과 일곱 갈래 — `crates/api/src/codemops.rs` 의 `verdict` 그대로. **글리프 ·
 *  문구 · 색 셋이 늘 함께 간다**(GitPage 와 같은 판단).
 *
 *  「붙었는데 도구가 없음」은 **오류가 아니다** — 주소도 토큰도 맞다. mops-code 쪽
 *  설정이 비어 있을 뿐이라, 빨갛게 칠하면 사람이 멀쩡한 주소부터 다시 본다. */
export const BANNER: Record<
  TestKind,
  { tone: "success" | "warning" | "error" | "info"; glyph: string; head: string; body: string }
> = {
  ok: { tone: "success", glyph: "✓", head: tx("conn.result.ok.head"), body: tx("code.result.ok") },
  "no-tools": { tone: "warning", glyph: "!", head: tx("code.result.noToolsHead"), body: tx("code.result.noTools") },
  unauthorized: { tone: "error", glyph: "✕", head: tx("conn.result.unauthorized.head"), body: tx("code.result.unauthorized") },
  "not-found": { tone: "error", glyph: "✕", head: tx("conn.result.notFound.head"), body: tx("code.result.notFound") },
  "http-error": { tone: "error", glyph: "✕", head: tx("conn.result.httpError.head"), body: tx("code.result.httpError") },
  unreachable: { tone: "error", glyph: "✕", head: tx("conn.result.unreachable.head"), body: tx("git.result.unreachable") },
  "no-address": { tone: "info", glyph: "·", head: tx("conn.result.noAddress.head"), body: tx("conn.result.noAddress.body") },
};

/** 패턴 페이지가 **이 순서대로** 일곱을 그린다 — GitPage 의 `TEST_KINDS` 와 같은 이유. */
export const TEST_KINDS: TestKind[] = [
  "ok",
  "no-tools",
  "unauthorized",
  "not-found",
  "http-error",
  "unreachable",
  "no-address",
];

export function CodePage() {
  const [conn, setConn] = useState<Conn | null>(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [token, setToken] = useState("");
  const [clearToken, setClearToken] = useState(false); // 「비우기」를 눌렀나 — 저장 때 빈 문자열을 보낸다
  const [open, setOpen] = useState(false); // 연결 띠가 펼쳐졌나
  const [result, setResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API}/admin/code-mops/connection`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((c: Conn) => {
        setConn(c);
        setBaseUrl(c.baseUrl);
        // **주소가 없으면 접어 둘 이유가 없다** — 처음 온 사람은 펼쳐진 것을 봐야 한다.
        if (!c.baseUrl) setOpen(true);
      })
      .catch((e: Error) => setLoadErr(e.message));
  }, []);

  function onTokenChange(v: string) {
    setToken(v);
    setClearToken(false); // 새로 적으면 「비우기」 의도는 취소된다
  }

  function clearTokenField() {
    setToken("");
    setClearToken(true);
  }

  async function test() {
    setTesting(true);
    setResult(null);
    try {
      const r = await fetch(`${API}/admin/code-mops/connection/test`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        /* **저장 없이 지금 칸의 값으로 시험한다**(GitPage·ConnPage 와 같은 규약). 토큰을
           안 적었으면 서버가 저장된 것을 쓴다 — 화면은 그 값을 모른다. */
        body: JSON.stringify({ baseUrl, token: token || undefined }),
      });
      setResult(await r.json());
    } catch (e) {
      setResult({ kind: "unreachable", toolCount: 0, detail: (e as Error).message });
    } finally {
      setTesting(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/code-mops/connection`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        /* 토큰은 **적었거나 「비우기」를 눌렀을 때만** 보낸다. 안 보내면 서버가 그대로
           두는데, 그래야 주소만 고치러 와서 저장했을 때 토큰이 안 날아간다. */
        body: JSON.stringify({ baseUrl, token: clearToken ? "" : token ? token : undefined }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const c: Conn = await r.json();
      setConn(c);
      setBaseUrl(c.baseUrl);
      setToken(""); // 저장한 토큰은 화면에 안 남긴다.
      setClearToken(false);
      setOpen(false);
    } catch (e) {
      setResult({ kind: "unreachable", toolCount: 0, detail: tx("settings.vendors.saveFailed", { err: (e as Error).message }) });
    } finally {
      setSaving(false);
    }
  }

  const b = result ? BANNER[result.kind] : null;

  return (
    <AppShell route="/code">
      <PageHead
        title={tx("nav.code")}
        lead={
          <>
            <p>{tx("code.lead1")}</p>
            <p>
              {rich(tx("code.lead2", hole("normal", "ro")), {
                normal: <b>{tx("common.status.normal")}</b>,
                ro: <b>{tx("code.readOnly")}</b>,
              })}
            </p>
          </>
        }
      />

      {loadErr ? <Alert tone="error">{tx("conn.loadFailed", { err: loadErr })}</Alert> : null}

      {/* ── 연결 띠 — 접힌 채로 선다(GitPage 의 틀을 그대로 쓴다) ────────────── */}
      <div className="git__strip">
        <div className="git__striphead">
          {conn?.baseUrl ? (
            <>
              <span className="git__glyph git__glyph--ok" aria-hidden="true">
                ✓
              </span>
              <b className="git__striphead-label">{tx("conn.result.ok.head")}</b>
              <span className="muted">
                {conn.baseUrl} · {tx(conn.tokenSet ? "git.tokenSet" : "git.tokenEmpty")}
                {result?.kind === "ok" ? ` · ${tx("code.toolCount", { n: result.toolCount })}` : ""}
              </span>
            </>
          ) : (
            <>
              <span className="git__glyph" aria-hidden="true">
                ·
              </span>
              <b className="git__striphead-label">{tx("git.notConnected")}</b>
              <span className="muted">{tx("git.notConnectedHint")}</span>
            </>
          )}
          <span className="git__spacer" />
          <button
            type="button"
            className="b-btn b-btn-secondary b-btn-sm git__toggle"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {tx("git.fix")} {open ? "⌃" : "⌄"}
          </button>
        </div>

        {open ? (
          <div className="git__form">
            <Field label={tx("code.address")} hint={tx("code.addressHint")}>
              {(id) => (
                <Input
                  id={id}
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="http://mops-code.local:8089/mcp"
                  inputMode="url"
                />
              )}
            </Field>

            <Field
              label={tx("chat.tokens")}
              hint={tx(clearToken ? "code.tokenHintClear" : conn?.tokenSet ? "code.tokenHintSet" : "code.tokenHintNone")}
            >
              {(id) => (
                <div className="cm__tokenrow">
                  <Input
                    id={id}
                    type="password"
                    value={token}
                    onChange={(e) => onTokenChange(e.target.value)}
                    placeholder={tx(conn?.tokenSet ? "conn.tokenPhSet" : "conn.tokenPhNone")}
                    autoComplete="off"
                  />
                  <Button size="lg" onClick={clearTokenField} disabled={!conn?.tokenSet && !token}>
                    {tx("code.clear")}
                  </Button>
                </div>
              )}
            </Field>

            <div className="git__actions">
              <Button variant="primary" onClick={test} loading={testing}>
                {tx("conn.test")}
              </Button>
              <Button onClick={save} loading={saving}>
                {tx("common.action.save")}
              </Button>
            </div>

            {b && result ? (
              <div className="git__banner">
                <Alert tone={b.tone}>
                  <span className="git__glyph" aria-hidden="true">
                    {b.glyph}
                  </span>{" "}
                  <b>{b.head}</b>
                  {result.status ? ` (${result.status})` : ""} — {b.body}
                  {result.kind === "ok" ? ` ${tx("code.okDetail", { n: result.toolCount, ms: result.ms ?? "?" })}` : ""}
                  {result.detail ? ` [${result.detail}]` : ""}
                </Alert>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* ── 이 설치가 아는 것 — 늘 선다, 연결 여부와 상관없이 ─────────────── */}
      <div className="cm__info">
        <b>{tx("code.knows.title")}</b>
        <ul>
          <li>{tx("code.knows.mcp")}</li>
          <li>{tx("code.knows.repo")}</li>
          <li>{tx("code.knows.index")}</li>
        </ul>
      </div>
      <p className="cm__note">
        <span aria-hidden="true">ⓘ</span> {tx("code.emptyNote")}
      </p>
    </AppShell>
  );
}
